import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Project-wide keyword search (PCA1-1). The magnifier after the last
// breadcrumb ([data-project-search-open]) opens a bar across the top; typing
// asks the server (debounced) for the grouped results panel.
export default class extends Controller {
  static targets = ["root", "input", "panel"]
  static values = { url: String, projectId: Number, phase: String }

  connect() {
    this._onDocClick = (e) => {
      if (!e.target.closest("[data-project-search-open]")) return
      e.preventDefault()
      this.open()
    }
    document.addEventListener("click", this._onDocClick)
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick)
    clearTimeout(this._timer)
    this._abort?.abort()
  }

  open() {
    this.rootTarget.classList.remove("hidden")
    requestAnimationFrame(() => { this.inputTarget.focus(); this.inputTarget.select() })
    track("Project Search Opened", { project_id: this.projectIdValue, project_phase: this.phaseValue })
  }

  close() {
    if (this.rootTarget.classList.contains("hidden")) return
    this.rootTarget.classList.add("hidden")
    this._abort?.abort()
  }

  search() {
    clearTimeout(this._timer)
    const q = this.inputTarget.value.trim()
    if (q.length < 2) {
      this.panelTarget.classList.add("hidden")
      this.panelTarget.innerHTML = ""
      return
    }
    this._timer = setTimeout(() => this._fetch(q), 250)
  }

  async _fetch(q) {
    this._abort?.abort()
    this._abort = new AbortController()
    try {
      const resp = await fetch(`${this.urlValue}?q=${encodeURIComponent(q)}`, {
        headers: { Accept: "text/html" }, signal: this._abort.signal
      })
      if (!resp.ok) return
      const html = await resp.text()
      if (this.inputTarget.value.trim() !== q) return // typed on since
      this.panelTarget.innerHTML = html
      this.panelTarget.classList.remove("hidden")
    } catch (e) {
      if (e.name !== "AbortError") console.error("[project-search]", e)
    }
  }

  // "All" shows 3 of each section; a section (tab or Show all) shows its 20.
  showTab(e) {
    const tab = e.currentTarget.dataset.tab
    const active = ["bg-brand", "text-white", "border-brand"]
    const idle = ["bg-surface-white", "text-text-body", "border-border-light", "hover:bg-gray-50"]
    this.panelTarget.querySelectorAll("[data-tab-btn]").forEach((b) => {
      const on = b.dataset.tab === tab
      active.forEach((c) => b.classList.toggle(c, on))
      idle.forEach((c) => b.classList.toggle(c, !on))
    })
    this.panelTarget.querySelectorAll("[data-group]").forEach((sec) => {
      sec.classList.toggle("hidden", tab !== "all" && sec.dataset.group !== tab)
      sec.querySelectorAll("[data-extra]").forEach((el) => el.classList.toggle("hidden", tab === "all"))
      sec.querySelector("[data-show-all]")?.classList.toggle("hidden", tab !== "all")
    })
  }

  openFirst(e) {
    const first = this.panelTarget.querySelector("a[data-search-hit]:not(.hidden)")
    if (!first) return
    e.preventDefault()
    first.click()
  }

  opened(e) {
    track("Project Search Result Opened", {
      project_id: this.projectIdValue, project_phase: this.phaseValue, section: e.currentTarget.dataset.section
    })
  }
}
